import _ from 'underscore';
import { useCallback, useReducer } from 'react';

import { mergeReducerStates } from './merge-reducer-state-util';

const AdPreviewModelActions = {
  ACTION_TYPE_RESET_STATE: 'ACTION_TYPE_RESET_STATE',
  ACTION_TYPE_UPDATE_IMAGE_CROPPING: 'ACTION_TYPE_UPDATE_IMAGE_CROPPING',
  ACTION_TYPE_UPDATE_PLACEMENT_CROPPING: 'ACTION_TYPE_UPDATE_PLACEMENT_CROPPING',
  ACTION_TYPE_UPDATE_PLACEMENT_CROPPING_BY_IMAGE_TYPE: 'ACTION_TYPE_UPDATE_PLACEMENT_CROPPING_BY_IMAGE_TYPE',
  ACTION_TYPE_SET_ACTIVE_IMAGES: 'ACTION_TYPE_SET_ACTIVE_IMAGES',
};

// interface TAdPreviewReducerState {
//   // there could be multiple images, each image has multiple placementSets, each placement set has a unique name
//   // and each placement set has multiple placements identifyed by placement type
//   // to find a placement, the path is [imageIndex][placementSetName][placementType]
//   previewImageListState: Array<TImageAndPlacementSets> | undefined | null;
//   hasDataChanged: boolean | undefined | null;
//
//   The activeImageIndices is used to support multiple image scenarios (for both Multi-Image placements and Multiple images to preview mentioned above).
//   It is an array because for multi-image placements, there may be more than one active image.
//   If this value is not set, all the images are considered as 'active'.
//   activeImageIndices: Array<number> | undefined | null;
// }

const ADPREVIEW_INIT_STATE /* : TAdPreviewReducerState */ = {
  previewImageListState: null,
  hasDataChanged: false,
  activeImageIndices: null,
};

// update crop settings for placement specified by placement-set name and placement-type
const updatePlacementCorpSettings = (
  imageAndPlacementSets, // : TImageAndPlacementSets,
  placementSetName, // : string,
  placementType, // : EPlacementType,
  cropping // : TCropRect
) /* : TImageAndPlacementSets */ => {
  const { previewImage, placementSets } = imageAndPlacementSets || {};
  let changed = false;
  // find the placement-set to update
  const newPlacementSets = _.map(placementSets, (placementSet) => {
    const { name, placementList } = placementSet || {};
    if (name === placementSetName) {
      // find the placement to update
      const newPlacementList = _.map(placementList, (placement) => {
        if (placement.placementMeta.placementType === placementType) {
          // found the placement to update
          if (!_.isEqual(placement.placementCropping, cropping)) {
            changed = true;
            return {
              ...placement, placementCropping: cropping, hasCroppingChanged: true, expandedPlacementCropping: undefined, effectiveImageUrl: undefined, effectiveImageWidth: undefined, effectiveImageHeight: undefined,
            };
          }
        }
        return placement;
      });
      // return a new placement-set with updated placement list
      return changed ? { name, placementList: newPlacementList } : placementSet;
    }
    return placementSet;
  });

  return changed ? { previewImage, placementSets: newPlacementSets } : imageAndPlacementSets;
};

// find all placements with the same image type, and update their crop settings
const updatePlacementCorpSettingsByImageType = (
  imageAndPlacementSets, // : TImageAndPlacementSets,
  placementSetName, // : string,
  imageType, // : EImageType,
  cropping // : TCropRect
) /* : TImageAndPlacementSets */ => {
  const { previewImage, placementSets } = imageAndPlacementSets || {};
  let changed = false;
  // find the placement-set to update
  const newPlacementSets = _.map(placementSets, (placementSet) => {
    const { name, placementList } = placementSet || {};
    if (name === placementSetName) {
      // find the placement to update
      const newPlacementList = _.map(placementList, (placement) => {
        if (placement.placementMeta.imageType === imageType) {
          // found the placement to update
          if (!_.isEqual(placement.placementCropping, cropping)) {
            changed = true;
            return {
              ...placement, placementCropping: cropping, hasCroppingChanged: true, expandedPlacementCropping: undefined, effectiveImageUrl: undefined, effectiveImageWidth: undefined, effectiveImageHeight: undefined,
            };
          }
        }
        return placement;
      });
      // return new placement-set with updated placement list
      return changed ? { name, placementList: newPlacementList } : placementSet;
    }
    return placementSet;
  });
  return changed ? { previewImage, placementSets: newPlacementSets } : imageAndPlacementSets;
};

function adPreviewReducer(state, action) {
  const { type: actionType, ...actionData } = action;
  switch (actionType) {
    case AdPreviewModelActions.ACTION_TYPE_RESET_STATE: {
      const { newState, forceToReset } = actionData;
      if (forceToReset) {
        const { previewImageListState, activeImageIndices, hasDataChanged } = newState || {};
        const resetState = {
          previewImageListState,
          hasDataChanged: hasDataChanged || false, // reset to false
          activeImageIndices,
        };
        return resetState;
      }

      const [changed, mergedState] = mergeReducerStates(state, newState);
      return !changed ? state : {
        ...mergedState,
        hasDataChanged: mergedState.hasDataChanged || false,
      };
    }

    case AdPreviewModelActions.ACTION_TYPE_UPDATE_IMAGE_CROPPING: {
      const { newImageAndPlacementSets, imageIndex } = actionData;
      const { previewImageListState } = state || {};
      if (previewImageListState && previewImageListState[imageIndex]) {
        const newState = [...previewImageListState];
        newState[imageIndex] = newImageAndPlacementSets;
        // mark the cropping data changed
        if (newState[imageIndex] && newState[imageIndex].previewImage) {
          newState[imageIndex].previewImage.hasCroppingChanged = true;
        }
        return {
          previewImageListState: newState,
          hasDataChanged: true,
        };
      }
      return state;
    }

    case AdPreviewModelActions.ACTION_TYPE_UPDATE_PLACEMENT_CROPPING: {
      const {
        placementType, cropping, imageIndex, placementSetName,
      } = actionData;
      const { previewImageListState: curState } = state || {};
      if (curState && curState[imageIndex]) {
        const imageAndPlacementSets = curState[imageIndex];
        const newImageAndPlacementSets = updatePlacementCorpSettings(imageAndPlacementSets, placementSetName, placementType, cropping);
        // check if data changed
        if (imageAndPlacementSets !== newImageAndPlacementSets) {
          const newState = [...curState];
          newState.splice(imageIndex, 1, newImageAndPlacementSets);
          return { previewImageListState: newState, hasDataChanged: true };
        }
      }
      return state;
    }

    case AdPreviewModelActions.ACTION_TYPE_UPDATE_PLACEMENT_CROPPING_BY_IMAGE_TYPE: {
      const {
        imageType, cropping, imageIndex, placementSetName,
      } = actionData;
      const { previewImageListState: curState } = state || {};
      if (curState && curState[imageIndex]) {
        const imageAndPlacementSets = curState[imageIndex];
        const newImageAndPlacementSets = updatePlacementCorpSettingsByImageType(imageAndPlacementSets, placementSetName, imageType, cropping);
        // check if data changed
        if (imageAndPlacementSets !== newImageAndPlacementSets) {
          const newPreviewImages = [...curState];
          newPreviewImages[imageIndex] = newImageAndPlacementSets;
          return { previewImageListState: newPreviewImages, hasDataChanged: true };
        }
      }
      return state;
    }

    case AdPreviewModelActions.ACTION_TYPE_SET_ACTIVE_IMAGES: {
      const { activeImageIndices } = actionData;
      const { previewImageListState, activeImageIndices: curActiveImageIndices } = state || {};
      const newIndices = _.isEmpty(previewImageListState) ? [] : _.chain(activeImageIndices)
        .uniq()
        .sortBy()
        .filter(i => _.isNumber(i) && i >= 0 && i < previewImageListState.length)
        .value();
      if (!_.isEqual(newIndices, curActiveImageIndices)) {
        return { ...state, activeImageIndices: newIndices };
      }
      return state;
    }
    default:
      return state;
  }
}

export const useAdPreviewReducer = (initState = ADPREVIEW_INIT_STATE) => {
  const [adPreviewModel, dispatch] = useReducer(adPreviewReducer, initState);
  const { previewImageListState, hasDataChanged, activeImageIndices } = adPreviewModel || {};

  // newState: {
  //  previewImageListState: Array<TImageAndPlacementSets> | undefined | null,
  //  activeImageIndices: Array<number> | undefined | null,
  //  hasDataChanged: boolean | undefined | null
  // }
  const resetReducerStates = useCallback((newState, forceToReset = false) => {
    dispatch({ type: AdPreviewModelActions.ACTION_TYPE_RESET_STATE, newState, forceToReset });
  }, []);

  const updateImageCropping = useCallback((newImageAndPlacementSets /* : TImageAndPlacementSets */, imageIndex = 0) => {
    dispatch({ type: AdPreviewModelActions.ACTION_TYPE_UPDATE_IMAGE_CROPPING, newImageAndPlacementSets, imageIndex });
  }, []);

  const updatePlacementCropping = useCallback((params) => {
    const {
      imageIndex = 0, placementSetName, placementType, cropping,
    } = params || {};
    dispatch({
      type: AdPreviewModelActions.ACTION_TYPE_UPDATE_PLACEMENT_CROPPING, placementType, cropping, imageIndex, placementSetName,
    });
  }, []);

  const updatePlacementCroppingByImageType = useCallback((params) => {
    const {
      imageIndex = 0, placementSetName, imageType, cropping,
    } = params || {};
    dispatch({
      type: AdPreviewModelActions.ACTION_TYPE_UPDATE_PLACEMENT_CROPPING_BY_IMAGE_TYPE, imageType, cropping, imageIndex, placementSetName,
    });
  }, []);

  const setActiveImageIndices = useCallback((...indices) => {
    dispatch({ type: AdPreviewModelActions.ACTION_TYPE_SET_ACTIVE_IMAGES, activeImageIndices: indices });
  }, []);

  return {
    previewImageListState,
    hasDataChanged,
    activeImageIndices,
    resetReducerStates,
    updateImageCropping,
    updatePlacementCropping,
    updatePlacementCroppingByImageType,
    setActiveImageIndices,
  };
};
